import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";

// The invoice's SIDE PANEL — 1.0's InvoicesPage boxes, moved off the main
// screen (Robert 2026-09-15: "for things going into the invoice it makes
// sense to have it show on the side panel, that way we don't pollute the main
// invoice screen"). Two fields, exactly 1.0's:
//
//   * INVOICE TO — 1.0's "Quoteable Invoice To", three lines. When line 1 says
//     something they REPLACE the addressee block on the invoice; the office
//     writes insurer / assessor company / job type ("AA Insurance / AA
//     Insurance / Callout Emergency Works" on 76580).
//   * INVOICE QUOTE HEADER — the paragraph printed above the lines, as typed,
//     carrying no amount ("STRIPOUT REQUEST - CHEMDRY - 02.09.26" and the
//     request under it).
//
// They are THE JOB'S, as in 1.0 (GPSLog.InvoiceToPostalAddress), so one entry
// prints on every invoice for the job, sent or not, imported or not — which
// is how 1.0 printed. Fields that SAVE, not print options: the document on
// the right redraws as you type and a box saves when you leave it. Never
// prints (no-print). Rules: invoices.md §11.

function Section({ title, children, hint }) {
  return (
    <div className="mb-3">
      <div className="text-uppercase small text-muted fw-semibold mb-1">{title}</div>
      {children}
      {hint ? <div className="small text-muted mt-1">{hint}</div> : null}
    </div>
  );
}

export const INVOICE_TO_FIELDS = ["invoice_to_1", "invoice_to_2", "invoice_to_3", "invoice_quote_header"];

// The printed shape of the four fields — the same rule as invoiceToShape in
// services/invoices.js, so the preview agrees with the server's read.
export function invoiceToPreview(fields) {
  const clean = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const lines = [fields.invoice_to_1, fields.invoice_to_2, fields.invoice_to_3].map(clean);
  return {
    invoice_to: lines[0] ? lines.filter(Boolean) : null,
    invoice_quote_header: clean(fields.invoice_quote_header),
  };
}

function InvoiceSidePanel({ invoice, detailed, onDetailed, onChange, onSave }) {
  // What the server last confirmed, so a blur only saves a real change and
  // "Saved" means it. Re-baselined when a different invoice opens.
  const saved = useRef({ id: null, fields: {} });
  const [status, setStatus] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    saved.current = { id: invoice.id, fields: pick(invoice) };
    setStatus("");
    setFailed(false);
  }, [invoice.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function pick(obj) {
    return Object.fromEntries(INVOICE_TO_FIELDS.map((k) => [k, obj[k] || ""]));
  }
  function change(key, value) {
    const fields = { ...pick(invoice), [key]: value };
    onChange({ [key]: value, ...invoiceToPreview(fields) });
  }
  async function blur() {
    const fields = pick(invoice);
    const trimmed = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.trim()]));
    const before = saved.current.fields;
    if (INVOICE_TO_FIELDS.every((k) => trimmed[k] === (before[k] || "").trim())) return;
    setStatus("Saving…");
    setFailed(false);
    try {
      await onSave(trimmed);
      saved.current.fields = trimmed;
      setStatus("Saved");
    } catch (err) {
      setStatus(err?.response?.data?.error || err?.message || "Unable to save.");
      setFailed(true);
    }
  }

  return (
    <div className="no-print quote-print-panel">
      {invoice.detail_lines?.length > 0 ? (
        <Section title="Document">
          <Form.Check
            type="switch"
            id="invoice-all-details"
            label="All details"
            checked={!!detailed}
            onChange={(e) => onDetailed(e.target.checked)}
            data-hint="Off = summary lines · on = the full line breakdown behind this invoice"
          />
        </Section>
      ) : null}

      <Section
        title="Invoice To"
        hint="Three lines that replace the addressee on every invoice for this job, e.g. insurer, assessor company, job type. Blank = the routing contact."
      >
        {["invoice_to_1", "invoice_to_2", "invoice_to_3"].map((key, i) => (
          <Form.Control
            key={key}
            size="sm"
            className="mb-1"
            placeholder={`To line ${i + 1}`}
            value={invoice[key] || ""}
            onChange={(e) => change(key, e.target.value)}
            onBlur={blur}
          />
        ))}
      </Section>

      <Section
        title="Invoice Quote Header"
        hint="Prints above the lines on every invoice for this job, exactly as typed."
      >
        <Form.Control
          as="textarea"
          rows={5}
          size="sm"
          value={invoice.invoice_quote_header || ""}
          onChange={(e) => change("invoice_quote_header", e.target.value)}
          onBlur={blur}
        />
      </Section>

      {status ? <div className={`small ${failed ? "text-danger" : "text-muted"}`}>{status}</div> : null}
    </div>
  );
}

export default InvoiceSidePanel;
